import { darken, Slider } from "@mui/material";
import { TicketType } from "../../TicketsPanel/Ticket";
import { FC, MouseEvent, useState } from "react";
import { DrawType, IDraw } from "../../../pages/DrawPage/DrawItem";
import { useSelector } from "../../../hooks";
import styles from "./SliderButton.module.scss";
import { DrawButton } from "../DrawButton/DrawButton";
import { getDrawState } from "../DrawCard";
import { ModalConfirm } from "../../Modal/ModalConfirm/ModalConfirm";
import sliderBlue from "./images/slider-blue.png";
import { resolveDrawCostCurrency } from "../../../pages/DrawPage/draws/draw-cost";

const generateSliderSx = (type: DrawType) => {
    const colorMap = {
        [DrawType.Rare]: {
            primary: '#5A96BA',
            light: '#E8F6FF',
            thumb: '#3A7B9E',
            mark: '#7AB6DA'
        },
        [DrawType.Epic]: {
            primary: '#D9A6DF',
            light: '#F8F9FF',
            thumb: '#B986BF',
            mark: '#E9C6ED'
        },
        [DrawType.Legendary]: {
            primary: '#BAAD5A',
            light: '#FFF9E8',
            thumb: '#BAAD5A',
            mark: '#DACD7A'
        }
    };

    if (!(type in colorMap))
        return;
    
    const colors = colorMap[type as keyof typeof colorMap];

    return {
        color: colors.primary,
        '& .MuiSlider-track': {
            background: colors.primary,
            border: 'none'
        },
        '& .MuiSlider-rail': {
            backgroundColor: `${colors.primary}80`,
        },
        '& .MuiSlider-thumb': {
            backgroundColor: colors.thumb,
        },
        '& .MuiSlider-mark': {
            backgroundColor: colors.mark,
            '&.MuiSlider-markActive': {
                backgroundColor: colors.primary
            }
        },
        '& .MuiSlider-markLabel': {
            color: darken(colors.primary, 0.25)
        },
        '& .MuiSlider-valueLabel': {
            backgroundColor: colors.primary,
            color: colors.light,
            '&:before': {
                borderBottomColor: colors.primary
            }
        }
    };
};

interface ISliderButtonProps {
    draw: IDraw;
    handler: (event: MouseEvent<HTMLButtonElement>, amount: number) => void;    
}

export const SliderButton: FC<ISliderButtonProps> = ({ draw, handler }) => {
    const [amount, setAmount] = useState(1);
    const [showConfirm, setShowConfirm] = useState(false);

    const tickets = useSelector(store => store.user.info.tickets);

    const currency = resolveDrawCostCurrency(draw.cost) as TicketType | null;
    const cost = currency ? draw.cost[currency] : 0;
    const maxValue = currency && cost > 0 ? Math.floor(tickets[currency] / cost) : 1;

    const marks = new Array(maxValue).fill(0).map((_, i) => {
        const value = i + 1;
        if (i === 0 || i === maxValue - 1) {
            return { value, label: value.toString() };
        }
        return { value };
    });

    const buy1TicketHandler = (event: MouseEvent<HTMLButtonElement>) => {
        setShowConfirm(false);
        handler(event, amount);
    }

    const cancelHandler = () => setShowConfirm(false);

    const localHandler = (event: MouseEvent<HTMLButtonElement>) => {
        const ticketType = resolveDrawCostCurrency(draw.cost);
        if (!ticketType)
            return;

        if (amount === 1 && tickets[ticketType] >= 3) {
            setShowConfirm(true);
            return;
        }
        
        handler(event, amount);
    }

    const getConfirmMessage = () => (
        <div>
            <img className={styles.helpImage} alt='slider' src={ sliderBlue } />
            <p>Можно использовать несколько билетов сразу — это увеличит твои шансы на победу!</p>
            <p>А ещё за каждый билет ты получишь +1 элемент в коллекцию.</p>
        </div>
    );
    
    return (
        <div>
            <div className={ `${styles.riseChance} ${styles[DrawType[draw.type]]}` }>
                <div>Шанс = (кол-во билетов) х (рейтинг удачи)</div>
            </div>
            
            <div className={ styles.sliderButtonCont }>
                <div className={ styles.sliderCont }>
                    <Slider
                        min={1}
                        max={maxValue}
                        step={1}
                        marks={marks}
                        defaultValue={1}
                        onChange={(_event, value) => setAmount(Array.isArray(value) ? value[0] : value)}
                        sx={{
                            '&.MuiSlider-root': { margin: 0 },
                            ...generateSliderSx(draw.type)
                        }}
                    />
                </div>

                <div style={{ flex: 3 }}>
                    <DrawButton
                        amount={ amount }
                        cost={ draw.cost }
                        handler={ localHandler }
                        drawState={ getDrawState(draw) }
                        type={ draw.type }
                    />
                </div>
            </div>

            { showConfirm &&
                <ModalConfirm
                    title='Покупка билета'
                    message={ getConfirmMessage() }
                    buttonYes={{ caption: 'Купить больше билетов', type: 'green', handler: cancelHandler }}
                    buttonNo={{ caption: 'Купить 1 билет', type: 'white', handler: (event) => buy1TicketHandler(event) }}
                />
            }
        </div>
    );
}